import React, { useState, useMemo, ReactNode, useEffect } from 'react'
import { format, isValid } from 'date-fns'
import {
  Link as RouterLink,
  match,
  NavLink,
  useHistory,
  useLocation,
} from 'react-router-dom'
import { useModal } from '@ebay/nice-modal-react'
import clsx from 'clsx'
import { ExternalLink, ArrowLeft, X, RefreshCcw } from 'react-feather'
import { PlusOutlined } from '@ant-design/icons'
import get from 'lodash.get'
import { useQuery } from '@tanstack/react-query'
import { Icon } from '@iconify/react'
import {
  Breadcrumb,
  BreadcrumbRoute,
  Button,
  Tag,
  Spin,
  useShowMessage,
  useFeedbackDetails,
  useGlobalState,
  Tooltip,
  PopOver,
  DesignSubmissionFiles,
  useUtils,
  useAssignment,
  usePaginatedAssignmentList,
  useGetTeamMembers,
  Avatar,
  AcceptSubmissionModal,
} from '@pepper/ui'
import { ASSIGNMENT_STATUS } from '@pepper/utils/src/constant/enum/assignment_status'
import { CLIENT_STAGES } from '@pepper/utils/src/constant/enum/assignment_stage'
import { getTagSetFromGroupedKey } from '@pepper/utils/src/function/getTagsetFromGroupKey'
import {
  getCreateProjectJSON,
  getUtilityEnums,
} from '@pepper/utils/src/api/utils/utils'
import getFormFields from '@pepper/utils/src/function/getFormFields'
import transformFields from '@pepper/utils/src/function/transformFields'
import { getVerticalKey } from '@pepper/utils/src/function/misc'
import VERTICAL_IDS from '@pepper/utils/src/constant/enum/vertical_ids'

import { useSelectedCompanyId } from 'utils/functions/misc'
import { useGetWorkflowStatus } from 'utils/hooks/workflows/useGetWorkflowStatus'
import { useGetWorkflowOnOffState } from 'utils/hooks/workflows/useGetWorkflowToggledState'
import DisplayDetails from 'components/DisplayDetails/DisplayDetails'
import ErrorTemplate from 'components/ErrorTemplate/ErrorTemplate'
import { getGroupedItems } from 'utils/functions/getGroupedItems'
import AssigneeDropdown from 'components/workflows/AssigneeDropdown'
import StatusDropdown from 'components/workflows/StatusDropdown'
import RouteManagement from './RouteManagement'
import CustomNavigation from './Navlink/Navlink'

/* assignment routes */
import Routes from './Routes'
import styles from './_.module.css'

interface AssignmentDetailsProps {
  match?: match<{ assignment_id: string }>
  assignmentDetails?: string
  keepSamePage?: boolean
  hideSideLoad?: () => void
  refetch?: () => void
}

export interface FieldKeys {
  value:
    | string
    | string[]
    | Attachment[]
    | undefined
    | { richText: string; attachments: { tag: string }[]; loomUrls: string[] }
  title: string
  type: string
  id?: string
  options?: { label: string; value: number | string }[] | BlockSelectOption[]
}

const AssignmentDetails = ({
  match: _match,
  assignmentDetails,
  keepSamePage,
  hideSideLoad,
  refetch: parentRefetch,
}: AssignmentDetailsProps): JSX.Element => {
  const history = useHistory<{ activeAssignmentPart?: string }>()
  const assignmentId = assignmentDetails || _match?.params.assignment_id || ''
  const [activeAssignmentPart, setActiveAssignmentPart] = useState<string>(
    history.location?.state?.activeAssignmentPart || 'activities'
  )
  const showMessage = useShowMessage()
  const location = useLocation()
  const companyId = useSelectedCompanyId()
  const { setTopbarComponent } = useGlobalState()
  const { project_categories } = useUtils()
  const [totalRecentActivity, setTotalRecentActivity] = useState<number>(0)
  const acceptAssignmentModal = useModal(AcceptSubmissionModal)
  const { data: assigneeList } = useGetTeamMembers({ companyId })
  const { data: workflowStatusList } = useGetWorkflowStatus({ companyId })
  const { data: toggleState } = useGetWorkflowOnOffState({ companyId })

  const {
    data: assignment,
    refetch,
    error,
    isLoading,
    // to avoid caching and bubbling effect in display new details
  } = useAssignment({ assignmentId, companyId, includeActivityLog: true })

  const { data: verticals = [] } = useQuery({
    queryKey: ['/utils/verticals'],
    queryFn: async () => {
      const result = await getUtilityEnums()

      if (result.status === 'error') {
        throw new Error('An error occurred when fetching the verticals.')
      }

      return get(result, 'data.verticals', [])
    },
  })

  const { records: assignments } = usePaginatedAssignmentList(
    {
      project_id: assignment?.project.id,
      group_id: assignment?.group_id,
      stage: ['all'],
    },
    25,
    true,
    false,
    typeof assignment !== 'undefined' ? true : false
  )

  const [group] = getGroupedItems(assignments)

  if (assignment?.timeline) {
    let latestAssigneeUpdated = false
    let firstAssigneeUpdated = true
    assignment?.timeline?.forEach((dataTimeline, timelineIndex) => {
      if (
        dataTimeline.status_code === ASSIGNMENT_STATUS.WRO_RJT.id &&
        timelineIndex > 1 &&
        !firstAssigneeUpdated
      ) {
        const assignee =
          assignment?.timeline[timelineIndex - 1]?.assignee_details

        if (assignee) {
          assignee.show_previous_assignee_rejected = true
        }
      }
      if (
        !latestAssigneeUpdated &&
        dataTimeline.status_code === ASSIGNMENT_STATUS.WRI_WRK.id
      ) {
        const assignee = assignment?.timeline[timelineIndex]?.assignee_details

        if (assignee) {
          assignee.show_latest_assignee = true
        }
        latestAssigneeUpdated = true
      }
      if (
        !latestAssigneeUpdated &&
        dataTimeline.status_code === ASSIGNMENT_STATUS.WRI_WRK.id
      ) {
        firstAssigneeUpdated = false
      }
    })
  }

  const [assignmentBrief, setAssignmentBrief] = useState<FieldKeys[]>([])

  const [projectBrief] = useState<FieldKeys[]>([])

  const [projectFormFields] = useState<BackendFormField[]>([])

  const { feedbacks } = useFeedbackDetails(assignmentId, {
    company_id: companyId,
  })

  const projectCategory = assignment?.category
  const verticalId =
    typeof assignment?.vertical.id === 'number' ? +assignment?.vertical.id : 0

  // fetch form fields base on selected vertical
  useEffect(() => {
    if (!verticalId) return
    const fetchFieldsBaseOnVertical = async () => {
      if (!Array.isArray(verticals)) return
      if (verticals.length === 0) return

      const verticalKey = getVerticalKey(verticalId, verticals)

      if (!verticalKey) return

      const response = await getCreateProjectJSON(
        verticalId,
        assignment?.json_version
      )
      const keySetContainingTagField = getTagSetFromGroupedKey({
        verticalResponse: response?.data,
      })
      const otherLinks =
        assignment?.other_links?.map((_otherLink: string) => {
          const splitItem = _otherLink?.split(',')
          return {
            tag: splitItem?.[0]?.split('tag:')?.[1] ?? '',
            link: splitItem?.[1]?.split('link:')?.[1] ?? '',
          }
        }) ?? []

      if (response.status === 'error') {
        throw new Error('Failed to fetch JSON file')
      }

      const result = getFormFields(response.data?.response)
      if (Array.isArray(result)) {
        const _fields: BackendFormField[] = transformFields(result)

        const _assignmentFields: FieldKeys[] = []
        _fields.forEach(field => {
          if (field.id === 'references') {
            _assignmentFields.push({
              type: field.type,
              title: field.label,
              value: Array.isArray(assignment?.reference_files)
                ? assignment?.reference_files.filter(file => !file.tag)
                : [],
              id: field.id,
              options: [],
            })
            return
          }
          let value = assignment?.[field.id] ?? ''
          if (
            field.type === 'html' &&
            (typeof assignment?.[field.id] === 'string' ||
              !assignment?.[field.id]) &&
            keySetContainingTagField?.[field.id]
          ) {
            const matchedTag = get(keySetContainingTagField, field.id, null)
            const defaultText = get(assignment, `${matchedTag?.tagField}`, '')
            const richText = get(assignment, field.id, defaultText)
            const referenceFiles = get(assignment, 'reference_files', [])
            const attachments = referenceFiles.filter(
              ({ tag }) => tag === matchedTag?.tag
            )
            const loomUrls =
              otherLinks
                ?.filter(
                  (otherLinkItem: { tag: string }) =>
                    otherLinkItem?.tag === matchedTag?.tag
                )
                ?.map((_otherLink: { link: string }) => _otherLink?.link) ?? []

            value = { richText, attachments, loomUrls }
          }

          _assignmentFields.push({
            type: field.type ?? '',
            title: field.label ?? '',
            value,
            id: field.id,
            options: field.options,
          })
        })

        setAssignmentBrief(_assignmentFields)
      }
    }
    fetchFieldsBaseOnVertical()
  }, [
    verticalId,
    assignment,
    showMessage,
    verticals,
    projectCategory,
    project_categories,
  ])

  /** need project id in url to highlight respective project in navbar */
  useEffect(() => {
    if (!keepSamePage && location.pathname && !isLoading && assignment) {
      history.replace({
        search: `?project_id=${assignment?.project.id}`,
      })
    }
  }, [location.pathname, isLoading, assignment, history, keepSamePage])

  /** Handle intersection observer */
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5,
    }
    const target = document.querySelector(`#assignment-header-block`)
    const callback = (entries: IntersectionObserverEntry[]) => {
      if (!keepSamePage) {
        if (entries[0] && !entries[0].isIntersecting) {
          setTopbarComponent(
            <BasicAssignmentDetails data={assignment} companyId={companyId} />
          )
        } else {
          setTopbarComponent(null)
        }
      }
    }
    const observer = new IntersectionObserver(callback, options)
    if (target) {
      observer.observe(target)
    }
    return () => {
      if (observer) {
        if (target) {
          observer.unobserve(target)
        }
        observer.disconnect()
        !keepSamePage && setTopbarComponent(null)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignment])

  const itemRender = (
    route: BreadcrumbRoute,
    _: unknown,
    routes: BreadcrumbRoute[]
  ): React.ReactNode => {
    const last = routes.indexOf(route) === routes.length - 1
    return last ? (
      <span className={styles.breadcrumbLink}>{route.breadcrumbName}</span>
    ) : (
      <RouterLink className={styles.breadcrumbLink} to={route.path}>
        {route.breadcrumbName}
      </RouterLink>
    )
  }

  const breadcrumbs = [
    {
      path: '/projects',
      breadcrumbName: 'All Projects',
    },
    {
      path: `/project/${assignment?.project.id}`,
      breadcrumbName: assignment?.project?.title ?? 'N/A',
    },
    {
      path: '',
      breadcrumbName: assignment?.assignment_title ?? '',
    },
  ]

  const links: { path: string; title: string; dataId?: string }[] = [
    {
      title: 'Assignment Brief',
      path: 'brief',
      dataId: 'assignmentsList_assignmentDrawer_briefTabButton',
    },
    {
      title: `Recent Activity (${totalRecentActivity})`,
      path: 'activities',
      dataId: 'assignmentsList_assignmentDrawer_recentActivityTabButton',
    },
    ...(toggleState?.data.workflow_enabled === 1
      ? [
          {
            title: 'Workflow Log',
            path: 'workflow-log',
            dataId: 'workflow_log',
          },
        ]
      : []),
    ...(group?.group?.item_count > 1
      ? [
          {
            title: `Linked Items (${group.group.item_count - 1})`,
            path: 'linked-items',
            dataId: 'assignmentsList_assignmentDrawer_linkedItemsTabButton',
          },
        ]
      : []),
  ]
  if (
    assignment?.status &&
    assignment.category_id === 1 &&
    !links.find(link => link.path === 'content') &&
    assignment?.submission_file?.src_type === 'pepper_editor' &&
    [ASSIGNMENT_STATUS.CLI_FIN.id, ASSIGNMENT_STATUS.CLI_AWA.id].includes(
      assignment.status
    )
  ) {
    links.splice(1, 0, {
      title: `Content`,
      path: 'content',
    })
  }

  const openAssignment = (_assignment?: Assignment) => {
    const isPepperEditor =
      _assignment?.submission_file?.src_type === 'pepper_editor'
    return isPepperEditor && companyId && _assignment?.id
      ? `/${companyId}/document/${_assignment.id}`
      : _assignment?.submission_file?.file_url
  }

  const details: {
    title: string
    value: string | ReactNode
  }[] = useMemo(() => {
    const deadline = {
      title: 'Deadline',
      value: (
        <span>
          {typeof assignment?.client_deadline === 'string' &&
          isValid(new Date(assignment?.client_deadline))
            ? format(new Date(assignment?.client_deadline ?? '-'), 'dd-MM-yyy')
            : '-'}
        </span>
      ),
    }

    const project = {
      title: 'Project ID',
      value: (
        <RouterLink to={`/project/${assignment?.project.id}`}>
          #{assignment?.project.code}
        </RouterLink>
      ),
    }

    return [deadline, project]
  }, [assignment?.client_deadline, assignment?.project])

  if (typeof error?.message === 'string' && /not found/i.test(error?.message)) {
    return (
      <ErrorTemplate
        statusCode={404}
        error={{
          title: '404',
          message: 'Error 404: Page Not Found',
        }}
      />
    )
  }

  if (
    typeof error?.message === 'string' &&
    /access forbidden/i.test(error?.message)
  ) {
    return (
      <ErrorTemplate
        statusCode={403}
        error={{
          title: '403',
          message: 'Error 403: Forbidden Access',
        }}
      />
    )
  }

  const submissionFile = openAssignment(assignment)

  const actionTag = (
    <div className={styles.actionsBtn}>
      {/* show give feedback button only if the client has not given any feedback */}
      {!feedbacks?.client_feedback &&
        // show feedback button when the client has approved the assignment
        (['approved'].includes(assignment?.stage ?? '') ? (
          <Button
            type="link"
            htmlType="button"
            icon={<PlusOutlined />}
            className={styles.feedbackBtn}
            onClick={() =>
              acceptAssignmentModal.show({
                assignmentIds: [assignmentId],
                companyId,
                refetch,
              })
            }
          >
            Give Feedback
          </Button>
        ) : (
          ''
        ))}
      {assignment?.vertical.id !== VERTICAL_IDS.SHORT_FORM_TRANSLATION &&
        ['approved', 'finished', 'rejected', 'rework'].includes(
          assignment?.stage ?? ''
        ) &&
        projectCategory?.key !== 'design' &&
        submissionFile && (
          <a href={submissionFile} target="_blank" rel="noopener noreferrer">
            <Button className={styles.viewSubmissionBtn} type="link">
              Review content <ExternalLink size={16} />
            </Button>
          </a>
        )}

      {['approved', 'finished', 'rejected', 'rework'].includes(
        assignment?.stage ?? ''
      ) &&
        projectCategory?.key === 'design' && (
          <PopOver
            content={() => {
              return (
                <DesignSubmissionFiles
                  fileList={assignment?.submission_file?.extras ?? []}
                />
              )
            }}
            trigger="click"
            placement="bottomLeft"
          >
            <Button className={styles.viewSubmissionBtn} type="link">
              View Submission <ExternalLink size={16} />
            </Button>
          </PopOver>
        )}
    </div>
  )

  const assigneeData = assigneeList
    ?.filter(_assignee => _assignee.id)
    ?.find(assignee => assignee.id === assignment?.workflow_assignee?.id)
  const workflowStatus = workflowStatusList?.find(
    status => status.id === assignment?.workflow_status?.id
  )

  return (
    <div
      className={clsx({
        [styles.assignmentWrap]: !keepSamePage,
      })}
    >
      <Spin spinning={isLoading} size="large">
        <div className={styles.assignmentBody}>
          <div
            className={clsx({
              [styles.assignmentContainer]: keepSamePage,
            })}
          >
            <div
              className={clsx(styles.assignmentDetails, {
                [styles.assignmentDetailsInbox]: keepSamePage,
              })}
              id="assignment-header-block"
            >
              {!keepSamePage && (
                <div
                  className={`${styles.breadcrumbContainer} ${styles.paddingL}`}
                >
                  <div className={styles.breadcrumb}>
                    <ArrowLeft
                      size={18}
                      onClick={() =>
                        history.push(`/project/${assignment?.project.id}`)
                      }
                    />
                    <Breadcrumb routes={breadcrumbs} itemRender={itemRender} />
                  </div>
                  {actionTag}
                </div>
              )}
              <div
                className={clsx({
                  [styles.assignmentHeader]: keepSamePage,
                })}
              >
                <div className={styles.assignmentTitle}>
                  <h1 className={styles.title}>
                    {assignment?.assignment_title ?? ''}
                    <span>
                      {assignment && assignment?.client_rework_count > 0 && (
                        <Tooltip
                          title={`This assignment has gone through ${assignment?.client_rework_count} reworks.`}
                          placement="bottom"
                        >
                          <Tag
                            className={clsx(styles.tag, styles.rework)}
                            icon={<RefreshCcw width="14px" height="14px" />}
                          >
                            {assignment?.client_rework_count}
                          </Tag>
                        </Tooltip>
                      )}
                    </span>
                  </h1>

                  <div className={styles.reviewContentWrap}>
                    {keepSamePage && actionTag}
                    {hideSideLoad && (
                      <X
                        onClick={() => hideSideLoad()}
                        className={styles.hideSideExpanded}
                        data-id="assignmentsList_assignmentDrawer_closeButton"
                      />
                    )}
                  </div>
                </div>
                <div className={styles.detailsContainer}>
                  <DisplayDetails details={details} tagData={assignment} />
                </div>
                {toggleState?.data.workflow_enabled === 1 && (
                  <div className={styles.workflows}>
                    <StatusDropdown
                      companyId={companyId}
                      statusList={workflowStatusList}
                      itemStatusId={assignment?.workflow_status.id}
                      itemId={assignmentId}
                      isPrivateDoc={false}
                    >
                      <div className={styles.status}>
                        <div
                          className={clsx(styles.statusColor, {
                            [styles.noColor]:
                              workflowStatus?.name === 'No Status',
                          })}
                          style={{
                            backgroundColor: `#${workflowStatus?.color}`,
                            outline: `3px solid #${workflowStatus?.color}25`,
                          }}
                        />
                        {workflowStatus?.name}
                        <Icon
                          icon="icon-park-solid:down-one"
                          className={styles.downIcon}
                        />
                      </div>
                    </StatusDropdown>
                    <AssigneeDropdown
                      companyId={companyId}
                      assigneeList={assigneeList}
                      itemId={assignmentId}
                      assigneeId={assignment?.workflow_assignee?.id}
                      isPrivateDoc={false}
                      showTooltip={true}
                    >
                      <div className={styles.assignee} role="none">
                        {!assigneeData ? (
                          <div className={styles.unassigned}>
                            <Icon icon="lucide:user" height={16} />
                          </div>
                        ) : assigneeData?.avatar ? (
                          <Avatar
                            src={assigneeData?.avatar}
                            className={styles.assigneeAvatar}
                          />
                        ) : (
                          <Avatar className={styles.assigneeAvatar}>
                            {assigneeData.full_name[0]}
                          </Avatar>
                        )}
                        {assigneeData?.full_name ?? 'No Assignee'}
                        <Icon
                          icon="icon-park-solid:down-one"
                          className={styles.downIcon}
                        />
                      </div>
                    </AssigneeDropdown>
                  </div>
                )}
                {!keepSamePage && (
                  <nav className={styles.tabContainer}>
                    {links.map(tab => (
                      <NavLink
                        to={tab.path}
                        key={tab.path}
                        className={styles.tab}
                        activeClassName={styles.active}
                      >
                        {tab.title}
                      </NavLink>
                    ))}
                  </nav>
                )}
                {keepSamePage && (
                  <CustomNavigation
                    active={activeAssignmentPart}
                    changeActive={page => setActiveAssignmentPart(page)}
                    link={links}
                    className={styles.tab}
                    activeClassName={styles.active}
                  />
                )}
              </div>
            </div>

            <section className={styles.assignmentTabDetails}>
              {!keepSamePage && assignment && (
                <Routes
                  projectBrief={projectBrief}
                  assignmentBrief={assignmentBrief}
                  projectFormFields={projectFormFields}
                  id={assignmentId}
                  data={assignment}
                  refetch={() => {
                    refetch()

                    if (typeof parentRefetch === 'function') {
                      parentRefetch()
                    }
                  }}
                  callBacks={{
                    setTotalRecentActivity,
                  }}
                />
              )}
              {keepSamePage && assignment && (
                <RouteManagement
                  projectBrief={projectBrief}
                  assignmentBrief={assignmentBrief}
                  projectFormFields={projectFormFields}
                  content={activeAssignmentPart}
                  id={assignmentId}
                  data={assignment}
                  refetch={() => {
                    refetch()

                    if (typeof parentRefetch === 'function') {
                      parentRefetch()
                    }
                  }}
                  callBacks={{
                    setTotalRecentActivity,
                  }}
                />
              )}
            </section>
          </div>
        </div>
      </Spin>
    </div>
  )
}

/** Private Component */
interface BasicAssignmentDetailsProps {
  data?: Assignment
  companyId: string
}
export const BasicAssignmentDetails: React.FC<BasicAssignmentDetailsProps> = ({
  data,
  companyId,
}) => {
  const openAssignment = (assignmentDataItem?: Assignment) => {
    const isPepperEditor =
      assignmentDataItem?.submission_file?.src_type === 'pepper_editor'
    return isPepperEditor && companyId && assignmentDataItem?.id
      ? `/${companyId}/document/${assignmentDataItem.id}`
      : assignmentDataItem?.submission_file?.file_url
  }

  const submissionFile = openAssignment(data)

  return (
    <div className={styles.basicDetailsContainer}>
      <h1>
        <Tooltip title={data?.assignment_title} placement="bottomLeft">
          {data?.assignment_title}
        </Tooltip>
      </h1>
      <div>
        {data ? (
          <Tag
            className={`m-0 ${styles.assignmentStatusTag}`}
            color={CLIENT_STAGES[data.stage]?.color}
          >
            {CLIENT_STAGES[data.stage]?.title}
          </Tag>
        ) : (
          ''
        )}
      </div>

      {/* Show review content section only in text and translation category */}
      <div>
        {data?.vertical.id !== VERTICAL_IDS.SHORT_FORM_TRANSLATION &&
          ['approved', 'finished', 'rejected', 'rework'].includes(
            data?.stage ?? ''
          ) &&
          data?.category?.key !== 'design' &&
          submissionFile && (
            <a href={submissionFile} target="_blank" rel="noopener noreferrer">
              <Button className={styles.viewSubmissionBtn} type="link">
                Review content <ExternalLink size={16} />
              </Button>
            </a>
          )}

        {/* Show review content section only in design category */}
        {['approved', 'finished', 'rejected', 'rework'].includes(
          data?.stage ?? ''
        ) &&
          data?.category?.key === 'design' && (
            <PopOver
              content={() => {
                return (
                  <DesignSubmissionFiles
                    fileList={data?.submission_file?.extras ?? []}
                  />
                )
              }}
              trigger="click"
              placement="bottomLeft"
            >
              <Button className={styles.viewSubmissionBtn} type="link">
                View Submission. <ExternalLink size={16} />
              </Button>
            </PopOver>
          )}
      </div>
    </div>
  )
}

export default AssignmentDetails
